import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type InterventionNature = {
  id: string;
  libelle: string;
  actif: boolean;
  ordre: number;
};

/** Natures ACTIVES triées (lecture pour le modal RDV). */
export function useNatures(enabled = true) {
  return useQuery({
    queryKey: ['natures'],
    queryFn: () => api.get<{ natures: InterventionNature[] }>('/natures'),
    select: (d) => d.natures,
    staleTime: 60_000,
    enabled,
  });
}

/** TOUTES les natures (actives + inactives) pour l'onglet admin. */
export function useAdminNatures() {
  return useQuery({
    queryKey: ['admin', 'natures'],
    queryFn: () => api.get<{ natures: InterventionNature[] }>('/admin/natures'),
    select: (d) => d.natures,
    staleTime: 60_000,
  });
}

function useInvalidateNatures() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['natures'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'natures'] });
  };
}

export function useCreateNature() {
  const invalidate = useInvalidateNatures();
  return useMutation({
    mutationFn: (libelle: string) =>
      api.post<{ nature: InterventionNature }>('/admin/natures', { libelle }),
    onSuccess: invalidate,
  });
}

export function useUpdateNature() {
  const invalidate = useInvalidateNatures();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { libelle?: string; actif?: boolean } }) =>
      api.patch<{ nature: InterventionNature }>(`/admin/natures/${id}`, data),
    onSuccess: invalidate,
  });
}

export function useDeleteNature() {
  const invalidate = useInvalidateNatures();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/natures/${id}`),
    onSuccess: invalidate,
  });
}

export function useReorderNatures() {
  const invalidate = useInvalidateNatures();
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.patch<{ natures: InterventionNature[] }>('/admin/natures/reorder', { orderedIds }),
    onSuccess: invalidate,
  });
}
